import React, { useCallback, useRef, useLayoutEffect, ReactElement } from 'react';
import {
  VariableSizeList as List,
  VariableSizeListProps as ListProps,
  ListItemKeySelector,
} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';

import * as UUID from '@/lib/uuid';
import { useCachedValue, useDependencyMap, usePreviousValue } from '@/lib/react-hooks';
import type { ClassComponentProps } from '@/types/react';

type InfiniteLoaderProps = ClassComponentProps<InfiniteLoader>;

export interface InfiniteListOwnProps {
  list: any[];
  renderItem: (item: this['list'][number], index: number) => ReactElement;
  hasNextPage: boolean;
  isNextPageLoading: boolean;
  loadNextPage: (...args: Parameters<InfiniteLoaderProps['loadMoreItems']>) => void|Promise<any>;
  estimatedRowHeight: number;
  rowHeightFn: (index: number) => number;
  height?: number;
  width?: number;
  loader?: ReactElement;
  loaderHeight?: number;
  paginationThreshold?: number;
  containerClassName?: string;
}

export type InfiniteListProps = InfiniteListOwnProps & {
  [key in keyof ListProps]?: ListProps[key];
};

const InfiniteListContainer = styled.div<{ fillSpace?: boolean }>`
  ${({ fillSpace }) => fillSpace && 'flex: 1;'}
`;

export const InfiniteList = ({
  list, renderItem, loader = <>Loading...</>,
  hasNextPage, isNextPageLoading, loadNextPage, paginationThreshold,
  height, width, estimatedRowHeight, rowHeightFn, loaderHeight, containerClassName,
  ...listProps
}: InfiniteListProps) => {
  const rowCount = hasNextPage ? list.length + 1 : list.length; // extra row for loader element

  const loadMoreRows = useCallback((...args: Parameters<typeof loadNextPage>) => {
    if (!isNextPageLoading) return loadNextPage(...args) || null;
    return null;
  }, [isNextPageLoading, loadNextPage]);

  const isRowLoaded = useCallback(
    (index: number) => !hasNextPage || index < list.length,
    [hasNextPage, list.length],
  );

  const keySelector = useCallback<ListItemKeySelector>((index) => list[index], [list]);

  const RowRenderer = useCallback(({ index, style }) => {
    let content: React.ReactChild;

    if (!isRowLoaded(index)) {
      content = loader;
    } else {
      content = renderItem(list[index], index);
    }

    return (
      <div key={list[index]} style={style}>
        {content}
      </div>
    );
  }, [list, isRowLoaded, renderItem]);

  const calcRowHeight = useCallback<ListProps['itemSize']>(
    (index) => (isRowLoaded(index) ? (rowHeightFn?.(index) ?? estimatedRowHeight) : loaderHeight),
    [isRowLoaded, rowHeightFn ?? estimatedRowHeight, loaderHeight],
  );

  const prevRowCount = usePreviousValue(rowCount);
  const prevHasNextPage = usePreviousValue(hasNextPage);
  const key = useCachedValue(
    UUID.generate,
    !hasNextPage && prevHasNextPage && prevRowCount === rowCount,
  );
  const listRenderer = useCallback<InfiniteLoaderProps['children']>(({ onItemsRendered, ref }) => (
    typeof height === 'number' && typeof width === 'number'
      ? (
        <InfiniteListContainer className={containerClassName}>
          <List
            // Set key to force list re-render when loader is removed and one ticket appended.
            // In such case rowCount remains the same and rowHeight does not get updated - lib bug.
            key={key}
            ref={ref}
            onItemsRendered={onItemsRendered}
            itemCount={rowCount}
            itemKey={keySelector}
            height={height}
            width={width}
            estimatedItemSize={estimatedRowHeight}
            itemSize={calcRowHeight}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...listProps}
          >
            {RowRenderer}
          </List>
        </InfiniteListContainer>
      ) : (
        <InfiniteListContainer className={containerClassName} fillSpace>
          <AutoSizer>
            {(calculated) => (
              <List
                key={key}
                ref={ref}
                onItemsRendered={onItemsRendered}
                itemCount={rowCount}
                itemKey={keySelector}
                height={height || calculated.height}
                width={width || calculated.width}
                estimatedItemSize={estimatedRowHeight}
                itemSize={calcRowHeight}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...listProps}
                style={{ maxWidth: 'unset' }}
              >
                {RowRenderer}
              </List>
            )}
          </AutoSizer>
        </InfiniteListContainer>
      )
  ), [
    RowRenderer, rowCount, height, width, calcRowHeight,
    containerClassName,
    useDependencyMap(listProps),
  ]);

  const ref = useRef();
  useLayoutEffect(() => {
    (ref.current as any)._listRef?.resetAfterIndex?.(0); // eslint-disable-line no-underscore-dangle
  }, [calcRowHeight]);
  return (
    <InfiniteLoader
      ref={ref}
      isItemLoaded={isRowLoaded}
      loadMoreItems={loadMoreRows}
      itemCount={rowCount}
      threshold={paginationThreshold}
    >
      {listRenderer}
    </InfiniteLoader>
  );
};
